import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Placeholder from "../../components/images/placeholder.png";
import { fetchPublicProjects } from "../../api/projects";
import { ResourceLoading } from "../../components/utils/ResourceLoading";
import { ResourceError } from "../../components/utils/ResourceError";
import { PageHeading } from "../../components/utils/pageHeading";
import { ContactCTA } from "../../components/home/ContactCTA";
import { PromangLogo } from "../../components/utils/PromangLogo";

/**
 * Projects listing — styled with the Airon "project card" grid from Airon.html.
 * Data is fetched from Promang (fetchPublicProjects) and displayed with a
 * live-feed badge so users know the list is dynamic.
 */
export const ProjectsPage = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetchPublicProjects({
          per_page: 100,
          sortBy: "created_at",
          sort: "DESC",
        });
        if (res?.data) {
          setProjects(Array.isArray(res.data) ? res.data : []);
        } else {
          setError("Kunne ikke hente prosjekter");
        }
      } catch (e) {
        console.error("Error loading projects:", e);
        setError("Kunne ikke hente prosjekter nå.");
      } finally {
        setIsLoading(false);
      }
    }
    load();
  }, []);

  const handleProjectClick = (projectId) => {
    navigate(`/om-oss/prosjekter/${projectId}`);
  };

  const getDisplayTitle = (project) =>
    project.public_title || `Prosjekt ${project.id}`;

  const getDisplayDescription = (project) => project.public_description || "";

  const getImageUrl = (project) => {
    if (project.public_main_image?.file?.url) return project.public_main_image.file.url;
    if (project.public_main_image?.file_url) return project.public_main_image.file_url;
    if (project.public_main_image?.url) return project.public_main_image.url;
    return null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.getFullYear();
  };

  const getDateRange = (project) => {
    const startYear = formatDate(project.start_date);
    const endYear = formatDate(project.end_date);
    if (startYear && endYear && startYear !== endYear)
      return `${startYear}-${endYear}`;
    return startYear || endYear || "";
  };

  /**
   * Bestemmer om prosjektet faktisk er ferdig — basert på eksplisitte
   * status/flagg-felt fra Promang, eller på at registrert sluttdato er passert.
   * Ingen av disse → ukjent/pågående, og vi viser da ingen tag.
   */
  const isProjectCompleted = (project) => {
    const statusStr = String(
      project.status || project.state || project.progress_status || "",
    ).toLowerCase();
    if (
      ["completed", "finished", "done", "ferdig", "levert", "closed", "delivered"].includes(
        statusStr,
      )
    ) {
      return true;
    }
    if (project.completed === true || project.is_completed === true) return true;
    if (project.finished_at || project.delivered_at || project.completed_at) return true;
    if (project.end_date) {
      const end = new Date(project.end_date);
      if (!Number.isNaN(end.getTime()) && end.getTime() <= Date.now()) {
        return true;
      }
    }
    return false;
  };

  const getStatusTag = (project) => {
    if (!isProjectCompleted(project)) return null;
    // Ferdig: bruk "Levert" når sluttdato er satt, ellers "Ferdig".
    return project.end_date
      ? { label: "Levert", live: false }
      : { label: "Ferdig", live: false };
  };

  return (
    <>
      <PageHeading
        eyebrow="02 / Prosjekter"
        title="Et utvalg av det vi har levert."
        description="Bolig, næring, skole og industri — her er noen av prosjektene vi har prosjektert, installert eller serviceavtalt. Listen synkes direkte fra Promang."
      />

      <section className="sec tint" style={{ borderTop: "none" }}>
        <div className="wrap">
          <div className="sec-head">
            <div className="sec-tag">Referanser</div>
            <h2>
              Prosjekter <em>vi er stolte av.</em>
            </h2>
            <div
              className="source-badge"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                whiteSpace: "nowrap",
              }}
              aria-label="Synkroniseres fra Promang"
            >
              <span className="pulse" />
              {isLoading ? (
                <span style={{ fontFamily: "var(--mono)", fontSize: 11 }}>
                  Henter fra
                </span>
              ) : (
                <span style={{ fontFamily: "var(--mono)", fontSize: 11 }}>
                  {projects.length} prosjekter · synk fra
                </span>
              )}
              <PromangLogo height={14} color="var(--airon-3)" />
            </div>
          </div>

          {isLoading ? (
            <div style={{ padding: "40px 0" }}>
              <ResourceLoading />
            </div>
          ) : error ? (
            <ResourceError errors={[error]} />
          ) : projects.length === 0 ? (
            <div
              style={{
                padding: 40,
                textAlign: "center",
                background: "#fff",
                border: "1px solid var(--line)",
                borderRadius: 16,
                color: "var(--muted)",
              }}
            >
              Ingen prosjekter tilgjengelig akkurat nå.
            </div>
          ) : (
            <div className="projects-grid">
              {projects.map((project) => {
                const imageUrl = getImageUrl(project);
                const title = getDisplayTitle(project);
                const description = getDisplayDescription(project);
                const dateRange = getDateRange(project);
                const status = getStatusTag(project);

                return (
                  <a
                    key={project.id}
                    className="project"
                    onClick={() => handleProjectClick(project.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <div
                      className="thumb"
                      data-label={`[ ${(title || "BILDE").toUpperCase()} ]`}
                      style={
                        imageUrl
                          ? {
                              backgroundImage: `url(${imageUrl})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                            }
                          : undefined
                      }
                    >
                      {imageUrl ? (
                        // hide the dashed placeholder text when we have a real image
                        <style>{`
                          .project .thumb[style*="background-image"]::before { display: none; }
                        `}</style>
                      ) : (
                        <img
                          src={Placeholder}
                          alt=""
                          style={{ display: "none" }}
                          aria-hidden="true"
                        />
                      )}
                      {status ? (
                        <span className={`tag ${status.live ? "live" : ""}`}>
                          {status.label}
                        </span>
                      ) : null}
                    </div>
                    <div className="body">
                      <div className="ttl">{title}</div>
                      {description ? (
                        <p
                          style={{
                            margin: "0 0 12px",
                            color: "var(--ink-2)",
                            fontSize: 14,
                            lineHeight: 1.5,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {description}
                        </p>
                      ) : null}
                      <div className="meta-row">
                        <span>
                          <b>{project.public_client || "Referanse"}</b>
                        </span>
                        <span>{dateRange || "—"}</span>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          )}

          {!isLoading && projects.length > 0 ? (
            <div
              style={{
                marginTop: 28,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <a
                href="https://promang.no"
                target="_blank"
                rel="noreferrer noopener"
                title="Powered by Promang"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  color: "var(--muted)",
                  textDecoration: "none",
                  fontFamily: "var(--mono)",
                  fontSize: 11,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
              >
                <span>Powered by</span>
                <PromangLogo height={16} color="var(--ink-2)" />
              </a>
            </div>
          ) : null}
        </div>
      </section>

      <ContactCTA
        title="Skal vi gjøre ditt bygg"
        highlight="til vårt neste prosjekt?"
        description="Vi tar en uforpliktende prat — vurderer behovet, foreslår løsninger og gir deg en fast pris."
      />
    </>
  );
};
